import { Link } from 'react-router-dom';
import { FileEdit } from 'lucide-react';

import { useWritableMinistries } from '../hooks/useProjectCatalogs';
import { buttonVariants } from '@/shared/components/ui/button';
import { messages } from '@/shared/constants/messages';

/**
 * O endereço da MESMA tela de rascunhos, dentro do Calendário.
 *
 * Uma constante porque três arquivos precisam concordar sobre ela — este
 * botão, a barra da grade e o descarte no formulário —, e o dia em que um
 * deles mandar para `/rascunhos` a pessoa troca de aba no meio da tarefa sem
 * nada na tela explicando por quê.
 */
export const DRAFTS_IN_CALENDAR_PATH = '/calendario/rascunhos';

/**
 * "Rascunhos" — o caminho até a lista de rascunhos de dentro do Calendário.
 *
 * ⚠️ **Ele existe porque o item de menu deixou de existir.** "Minhas pendências"
 * era item de topo da sidebar (decisão 97) e saiu para todos os papéis: o que
 * volta a pedir resposta do líder — projeto devolvido para ajuste — já chega
 * pela caixa de entrada do Calendário, com o verbo ao lado, e um item de menu
 * repetindo a mesma fila cobrava uma consulta por sessão para dizer o que a
 * coluna da esquerda já dizia. O que **não** chega pela caixa é o rascunho:
 * ele não é intenção ainda (regra 8), nunca entra na grade, e sem este botão
 * ficaria inalcançável para quem não tem "Projetos" no menu.
 *
 * **Sem contador, de propósito.** Contador é regra de item de FILA na sidebar
 * (ADR-012 regra 2); aqui é atalho de barra, e um número exigiria justamente a
 * consulta por sessão que a remoção do item eliminou.
 *
 * O gate é o mesmo de `NewProjectButton`, e pela mesma razão: quem não escreve
 * em ministério nenhum não tem rascunho — a lista abriria vazia por construção.
 * Quem responde é a RPC, não o front.
 *
 * ⚠️ **O destino é do CHAMADOR, e é o que consertou a troca de aba.** O botão
 * apontava para `/rascunhos` nos dois lugares em que aparece; para quem tem
 * "Projetos" no menu (`admin`, `overseer`, `coordinator`) clicá-lo dentro do
 * Calendário apagava o Calendário da sidebar. Agora a barra da grade manda
 * para `DRAFTS_IN_CALENDAR_PATH` e o cabeçalho de `/projetos` para o endereço
 * de sempre: cada aba fica com a sua cópia, e nenhuma das duas empurra a
 * pessoa para fora do que ela estava fazendo.
 */
interface DraftsButtonProps {
  /** Padrão: a gaveta de "Projetos". O Calendário passa a sua. */
  to?: string;
}

export function DraftsButton({ to = '/rascunhos' }: DraftsButtonProps) {
  const { data: ministries } = useWritableMinistries();
  if (!ministries || ministries.length === 0) return null;

  return (
    <Link to={to} className={buttonVariants({ variant: 'outline' })}>
      <FileEdit aria-hidden="true" className="size-4" />
      {messages.projects.draftsTitle}
    </Link>
  );
}
